import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function ExportModule() {
  const skin = useSkin();

  const [exportType, setExportType] = useState("patrols");
  const [dateRange, setDateRange] = useState("30");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [guardId, setGuardId] = useState("");
  const [routeId, setRouteId] = useState("");

  const [data, setData] = useState([]);
  const [guards, setGuards] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= INIT ================= */
  useEffect(() => {
    fetchFilters();
    fetchData();
  }, []);

  /* ================= FETCH ================= */
  const fetchFilters = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/reports/export/filters`);
      setGuards(res.data.guards || []);
      setRoutes(res.data.routes || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE_URL}/reports/export`, {
        params: {
          exportType,
          dateRange,
          startDate,
          endDate,
          guardId,
          routeId,
        },
      });

      setData(res.data || []);
      setError(res.data.length === 0 ? "No data found" : "");
    } catch (err) {
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  /* ================= EXPORT ================= */
  const exportCSV = () => {
    if (!data.length) return alert("No data");

    const headers = Object.keys(data[0]);
    const rows = data.map((row) => headers.map((h) => row[h]));

    const csv =
      [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "export.csv";
    a.click();
  };

  const exportExcel = () => {
    if (!data.length) return alert("No data");

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Export");
    XLSX.writeFile(wb, "export.xlsx");
  };

  return (
    <Layout activePage="exports">
      <PageHeader />

      {/* FILTERS */}
      <FormSection title="Export Filters">
        <div className="grid md:grid-cols-4 gap-4">
          <Select value={exportType} onChange={(e) => setExportType(e.target.value)}>
            <option value="patrols">Patrols</option>
            <option value="incidents">Incidents</option>
            <option value="checkpoints">Checkpoints</option>
            <option value="guards">Guards</option>
          </Select>

          <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="7">7 Days</option>
            <option value="30">30 Days</option>
            <option value="custom">Custom</option>
          </Select>

          {dateRange === "custom" && (
            <>
              <Input type="date" onChange={(e) => setStartDate(e.target.value)} />
              <Input type="date" onChange={(e) => setEndDate(e.target.value)} />
            </>
          )}

          <Select onChange={(e) => setGuardId(e.target.value)}>
            <option value="">All Guards</option>
            {guards.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Select>

          <Select onChange={(e) => setRouteId(e.target.value)}>
            <option value="">All Routes</option>
            {routes.map((r) => (
              <option key={r.route_id} value={r.route_id}>
                {r.r_name}
              </option>
            ))}
          </Select>

          <button
            onClick={fetchData}
            className="px-4 py-3 rounded-xl text-white"
            style={{ background: skin.gradient }}
          >
            Refresh
          </button>
        </div>
      </FormSection>

      {/* EXPORT BUTTONS */}
      <FormSection title="Export Options">
        <div className="flex gap-4">
          <button
            onClick={exportCSV}
            className="px-4 py-3 rounded-xl text-white"
            style={{ background: skin.gradient }}
          >
            Export CSV
          </button>

          <button
            onClick={exportExcel}
            className="px-4 py-3 rounded-xl text-white"
            style={{ background: skin.gradient }}
          >
            Export Excel
          </button>
        </div>
      </FormSection>

      {/* TABLE */}
      <TableSection title="Preview Data">
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="text-gray-400">{error}</p>
        ) : data.length ? (
          <table className="w-full">
            <thead>
              <tr>
                {Object.keys(data[0]).map((key) => (
                  <Th key={key}>{key}</Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  {Object.values(row).map((val, j) => (
                    <Td key={j}>{val}</Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyRow col={5} />
        )}
      </TableSection>
    </Layout>
  );
}

/* ================= UI COMPONENTS ================= */

function PageHeader() {
  return (
    <div className="mb-6">
      <h2 style={{ fontFamily: FONT_HEADING }}>Export Module</h2>
      <p style={{ fontFamily: FONT_UI }}>
        Export patrol, incidents, checkpoints, and guard data
      </p>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="bg-white p-6 rounded-2xl mb-6">
      <h3 className="mb-4" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function TableSection({ title, children }) {
  return (
    <div className="bg-white p-6 rounded-2xl mb-6 overflow-x-auto">
      <h3 className="mb-4" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Input(props) {
  return <input {...props} className="border p-3 rounded-xl w-full" />;
}

function Select(props) {
  return <select {...props} className="border p-3 rounded-xl w-full" />;
}

function Th({ children }) {
  return <th className="p-3 text-left bg-slate-100">{children}</th>;
}

function Td({ children }) {
  return <td className="p-3 border-b">{children}</td>;
}

function EmptyRow({ col }) {
  return (
    <tr>
      <td colSpan={col} className="text-center p-5 text-gray-400">
        No data available
      </td>
    </tr>
  );
}